import { roleSatisfies } from '@/lib/roleRank';

// Edit modality — how a surface is actually changed. Ordered strongest → weakest:
// the console is an editing tool, so this is the primary signal on every item.
// See docs/superpowers/specs/2026-07-21-console-ia-editability-design.md.
export type Modality = 'sign' | 'form' | 'json' | 'code' | 'derived';
// Edit-modality badge → the mockup `.mod-*` class (styled in console-ui.css).
// `label` is the short chip text; retheme via the --ui-* variables in that file.
export const MODALITY: Record<Modality, { label: string; cls: string; title: string }> = {
  sign: { label: 'sign', cls: 'mod-sign', title: 'form + sign-off — typed widgets, allowlist-bounded; edit and sign are one act' },
  form: { label: 'form', cls: 'mod-form', title: 'structured form edited in-console; no sign-off gate' },
  json: { label: 'json', cls: 'mod-json', title: 'raw JSON textarea, schema by hand — transitional, slated to become a form' },
  code: { label: 'code', cls: 'mod-code', title: 'authored in code/seed → PR → redeploy; browse + flag feedback only' },
  derived: { label: 'calc', cls: 'mod-calc', title: 'derived — computed by the engine, never edited' },
};

// Taxonomy-layer key — the sidebar/breadcrumb dot colour comes from a `.dot.<key>`
// class in console-ui.css (themeable via the --ui-* variables there).
export type LayerKey = 'needs' | 'lens' | 'data' | 'action' | 'board' | 'safety';

export type NavItem = { to: string; label: string; requires: 'advisor' | 'admin'; end?: boolean; mod?: Modality };
export type NavGroup = { title: string; sub?: string; color?: LayerKey; items: NavItem[] };

// Grouped by the canonical taxonomy layers; a cross-cutting "Needs you" work lane
// on top; ordered so what the advisor can act on floats up. Layer colours mirror
// the IA design mock.
export const NAV_GROUPS: NavGroup[] = [
  { title: 'Needs you', sub: 'your open work', color: 'needs', items: [
    { to: '/', label: 'Review queue', requires: 'advisor', end: true },
    { to: '/review/peri', label: 'Perimenopause review', requires: 'advisor' },
    { to: '/registries/peri_attribution_registry', label: 'Peri attribution', requires: 'advisor', mod: 'sign' },
    { to: '/registries/mode_contraindications', label: 'Mode contraindications', requires: 'advisor', mod: 'sign' },
    { to: '/registries/genomic_interpretations', label: 'Genomic interpretations', requires: 'advisor', mod: 'sign' },
    { to: '/registries/deficiency_allowlist', label: 'Deficiency allowlist', requires: 'advisor', mod: 'sign' },
    { to: '/registries/safety_index', label: 'Safety Index · cells', requires: 'advisor', mod: 'sign' },
  ] },
  { title: 'Lens', sub: 'frames everything', color: 'lens', items: [
    { to: '/admin/modes', label: 'Modes', requires: 'advisor', mod: 'code' },
    { to: '/admin/schools', label: 'Research schools', requires: 'advisor', mod: 'code' },
  ] },
  { title: 'Data', sub: 'what we know', color: 'data', items: [
    { to: '/admin/domains', label: 'Domains', requires: 'advisor', mod: 'code' },
    { to: '/admin/panels', label: 'Panels', requires: 'advisor', mod: 'code' },
    { to: '/admin/biomarkers', label: 'Biomarkers', requires: 'advisor', mod: 'code' },
    { to: '/admin/unmatched-biomarkers', label: 'Unmatched biomarkers', requires: 'admin', mod: 'form' },
    { to: '/admin/instruments', label: 'Instruments', requires: 'advisor', mod: 'code' },
  ] },
  { title: 'Actions', sub: 'what to do', color: 'action', items: [
    { to: '/admin/tasks', label: 'Tasks', requires: 'advisor', mod: 'code' },
  ] },
  { title: 'Board output', sub: 'deliberated', color: 'board', items: [
    { to: '/admin/intervention-classes', label: 'Intervention classes', requires: 'advisor', mod: 'code' },
    { to: '/admin/renderers', label: 'Renderers', requires: 'advisor', mod: 'code' },
  ] },
  { title: 'Safety & formulation', sub: 'cross-cutting', color: 'safety', items: [
    { to: '/admin/safety-tables', label: 'Safety tables', requires: 'advisor', mod: 'code' },
    { to: '/companion-safety', label: 'Companion safety — visibility', requires: 'advisor', mod: 'code' },
    { to: '/admin/safety-index', label: 'Safety Index — entries', requires: 'advisor', mod: 'json' },
    { to: '/admin/cpic', label: 'Pharmacogenomics', requires: 'admin', mod: 'json' },
    { to: '/admin/genomics', label: 'Genomics (SNPs)', requires: 'advisor', mod: 'code' },
    { to: '/admin/compounds', label: 'Compounds', requires: 'admin', mod: 'form' },
  ] },
  { title: 'Review', items: [
    { to: '/proposals/reference-range', label: 'Reference ranges', requires: 'advisor', mod: 'form' },
    { to: '/audit', label: 'Audit', requires: 'advisor' },
  ] },
  { title: 'Research database', items: [
    { to: '/moderation', label: 'Moderation', requires: 'advisor', mod: 'form' },
    { to: '/ingested', label: 'Ingested corpus', requires: 'admin' },
  ] },
  { title: 'Access', items: [
    { to: '/admin/testers', label: 'Testers & invites', requires: 'admin', mod: 'form' },
  ] },
  // Route A's manual fallback: hand placement, holds and unmatched placements.
  // Admin only, here and at core's router: it shows people's referral details.
  { title: 'Pathology', sub: 'route A orders', items: [
    { to: '/admin/pathology', label: 'Pathology orders', requires: 'admin', mod: 'form' },
  ] },
  // The closed-beta run surface. Advisors read it (the preflight verdict and the
  // rubric are MA-relevant); gate/date/finding writes are admin-gated in-page and
  // server-side. Clinical review is NOT here — it lives at /review/peri.
  { title: 'Closed beta', sub: 'the peri run', items: [
    { to: '/beta', label: 'Peri beta console', requires: 'advisor', mod: 'form' },
  ] },
];

export function visibleNav(role: string): NavGroup[] {
  return NAV_GROUPS
    .map((g) => ({ ...g, items: g.items.filter((i) => roleSatisfies(role, i.requires)) }))
    .filter((g) => g.items.length > 0);
}

// Resolve a pathname to the nav surface it belongs to (longest `to` prefix),
// so pages can derive their breadcrumb + edit-modality badge from the route
// without wiring each one by hand.
export function surfaceForPath(pathname: string): { group: NavGroup; item: NavItem } | null {
  let best: { group: NavGroup; item: NavItem } | null = null;
  for (const group of NAV_GROUPS) {
    for (const item of group.items) {
      const hit = item.to === pathname || (item.to !== '/' && pathname.startsWith(item.to + '/'));
      if (hit && (!best || item.to.length > best.item.to.length)) best = { group, item };
    }
  }
  return best;
}
